import { useQuery } from 'react-query';
import { getStatistics, getWhatsNext } from '../../services/dashboard';
import { getNotifications } from '../../services/notifications';
import { useCMSData } from '../../state/content-manager';
import {
  NotificationList,
  PageLayout,
  Statistics,
  Loader,
  useAuthData,
  StatsDurations,
} from '../../components';
import { WhatsNext } from './WhatsNext';
import { breakpoint, Heading } from '@grays-npm/component-library';
import styled from 'styled-components';
import { useState } from 'react';
import { format, subDays } from 'date-fns';
import { LinkToText } from '../styles';
import { QUERY_KEYS } from 'utils/constants';

const StyledHeading = styled(Heading)`
  ${({ theme }) => theme.typography.headings[4]['xl']};

  ${breakpoint('lg')`
    ${({ theme }) => theme.typography.headings[3]['xl']};
  `}
`;

const StyledSubHeading = styled(Heading)`
  margin: ${({ theme }) => theme.spacings[4].rem} 0;

  ${breakpoint('lg')`
    margin: ${({ theme }) => theme.spacings[5].rem} 0;
  `}
`;

const NotificationHeadingContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Dashboard = () => {
  const { token } = useAuthData();
  const [duration, setDuration] = useState<StatsDurations>(
    StatsDurations.Thirty
  );

  // Queries
  const notificationsQuery = useQuery([QUERY_KEYS.NOTIFICATIONS, token], () => {
    const queryParams = {
      pageNumber: '1',
      pageSize: '5',
      includeRead: 'false',
    };
    return getNotifications(token, queryParams);
  });

  const whatsNextQuery = useQuery(QUERY_KEYS.WHATS_NEXT, getWhatsNext);

  const statsQuery = useQuery([QUERY_KEYS.STATISTICS, token, duration], () => {
    const today = new Date();
    const queryParams = {
      FromDate: format(subDays(today, Number(duration)), 'yyyy-MM-dd'),
      ToDate: format(today, 'yyyy-MM-dd'),
    };
    return getStatistics(token, queryParams);
  });

  const { dashboard } = useCMSData();

  const {
    title,
    seeMore: showMoreText,
    empty: emptyText,
  } = dashboard.notifications;

  const showNotifications =
    notificationsQuery?.data?.data && notificationsQuery.data.data.length > 0;

  return (
    <PageLayout>
      <StyledHeading size={4}>{dashboard.title}</StyledHeading>
      <Loader query={statsQuery}>
        {statsQuery.data && (
          <Statistics
            statistics={statsQuery.data}
            duration={duration}
            onChange={(option) => setDuration(option as StatsDurations)}
          />
        )}
      </Loader>

      <Loader query={notificationsQuery}>
        <NotificationHeadingContainer>
          <StyledSubHeading size={4}>{title}</StyledSubHeading>
        </NotificationHeadingContainer>
        {notificationsQuery.data?.data && (
          <NotificationList
            notifications={notificationsQuery.data.data}
            emptyText={emptyText}
          />
        )}
        {showNotifications && (
          <LinkToText
            to="/notifications"
            size="md"
            context="secondary"
            isUnderlined={false}
          >
            {showMoreText}
          </LinkToText>
        )}
      </Loader>
      <Loader query={whatsNextQuery}>
        {whatsNextQuery.data && (
          <WhatsNext cardContents={whatsNextQuery.data} />
        )}
      </Loader>
    </PageLayout>
  );
};
